function getClerkAccountPortalHost(frontendApiHost: string) {
    if (frontendApiHost.startsWith('clerk.')) {
        return `accounts.${frontendApiHost.slice('clerk.'.length)}`
    }

    return frontendApiHost.replace('.clerk.', '.')
}

/**
 * Hosts of the Clerk instance derived from the publishable key:
 * the Frontend API host (e.g. key-phoenix-5.clerk.accounts.dev) and the
 * Account Portal host (e.g. key-phoenix-5.accounts.dev or accounts.superscale.ai),
 * where OAuth consent pages live.
 */
export function getClerkInstanceHosts(): string[] {
    const publishableKey = process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY
    if (!publishableKey) return []

    try {
        const encoded = publishableKey.replace(/^pk_(test|live)_/, '')
        const frontendApiHost = atob(encoded).replace(/\$$/, '')
        const accountPortalHost = getClerkAccountPortalHost(frontendApiHost)
        return [frontendApiHost, accountPortalHost]
    } catch {
        return []
    }
}

/** Exact HTTPS origins Clerk may return to while completing its own OAuth consent flow. */
export function getClerkInstanceOrigins(): string[] {
    return [...new Set(getClerkInstanceHosts().map((hostname) => `https://${hostname}`))]
}

/** Redirect origins accepted by Clerk after authentication, including its own exact instance hosts. */
export function getClerkAllowedRedirectOrigins(): string[] {
    return [
        ...(process.env.NEXT_PUBLIC_WEB_URL ? [process.env.NEXT_PUBLIC_WEB_URL] : []),
        ...getClerkInstanceOrigins(),
        ...(process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production'
            ? [
                  'http://localhost:3000',
                  ...(process.env.NEXT_PUBLIC_MOBILE_LAN_ORIGIN ? [process.env.NEXT_PUBLIC_MOBILE_LAN_ORIGIN] : []),
              ]
            : []),
    ]
}

/** True if the URL is https and its hostname is exactly one of the given hosts. */
export function isTrustedHttpsUrl(url: string, hostnames: string[]): boolean {
    try {
        const parsedUrl = new URL(url)
        return parsedUrl.protocol === 'https:' && hostnames.includes(parsedUrl.hostname)
    } catch {
        return false
    }
}

/** Returns true if the URL points at this app's own Clerk instance (FAPI or Account Portal). */
export function isClerkInstanceUrl(url: string): boolean {
    return isTrustedHttpsUrl(url, getClerkInstanceHosts())
}

/**
 * Validates a redirect URL to prevent open redirect vulnerabilities.
 * A URL is considered safe if:
 * 1. It's a relative path (starts with / but not //)
 * 2. It's a trusted external domain (e.g., chatgpt.com, openai.com)
 * 3. It's this app's own Clerk instance (OAuth authorize/consent continuations)
 * 4. It's an exact known native OAuth client callback (e.g., Cursor MCP)
 *
 * @param url - The URL to validate
 * @returns The URL if safe, or null if unsafe
 */
export function getSafeRedirectUrl(url: string | null): string | null {
    if (!url) return null

    const trustedNativeCallbacks = ['cursor://anysphere.cursor-mcp/oauth/callback']
    if (trustedNativeCallbacks.includes(url)) {
        return url
    }

    // Check if it's a relative URL (starts with / but not //)
    // The // check prevents protocol-relative URLs like //evil.com
    if (url.startsWith('/') && !url.startsWith('//')) {
        return url
    }

    if (isClerkInstanceUrl(url)) {
        return url
    }

    // Check if it's an absolute URL pointing to a trusted domain
    try {
        const parsedUrl = new URL(url)

        // Only allow https for security
        if (parsedUrl.protocol !== 'https:') {
            return null
        }

        // Trusted domains for ChatGPT/OpenAI OAuth flows
        // Note: clerk.accounts.dev is needed because the OAuth flow goes through Clerk's
        // authorization endpoint first before redirecting to ChatGPT
        const trustedDomains = [
            'chatgpt.com',
            'openai.com',
            'clerk.accounts.dev',
            'app.superscale.ai',
            'superscale.ai',
            'app.staging.superscale.ai',
        ]

        const isTrusted = trustedDomains.some(
            (domain) => parsedUrl.hostname === domain || parsedUrl.hostname.endsWith('.' + domain)
        )

        if (isTrusted) {
            return url
        }
    } catch {
        // Not a valid absolute URL
    }

    return null
}
